/**
 * @voltxampere/web — `<vxa-toast-stack>` (Sprint 25 R2 lane 1)
 *
 * Real toast notification stack. Replaces the Sprint 11 placeholder
 * (`render() return html\`\``) per TS-007 (Sprint 25 R1 triage).
 *
 * Subscribes to the `toasts` signal in `store/toasts.ts` and renders
 * a fixed-position stack at the bottom-right of the viewport. Each
 * toast carries a kind variant (error / warning / info / success), an
 * optional title, a message, an icon glyph, and a manual close button.
 *
 * Accessibility:
 *   - Outer wrapper is a `role="region"` landmark with an i18n label.
 *   - Errors render as `role="alert"` + `aria-live="assertive"` so
 *     screen readers announce them immediately. Other kinds use
 *     `role="status"` + `aria-live="polite"`.
 *   - Close button has an i18n `aria-label`.
 *
 * z-index: `10000`, deliberately below the modal-root (`1_000_000`)
 * so a toast does not paint over a modal but does paint above all
 * normal UI surfaces.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { effect } from "@preact/signals-core";

import { t } from "../i18n/index.js";
import { dismissToast, toasts, type Toast, type ToastKind } from "../store/toasts.js";

@customElement("vxa-toast-stack")
export class VxaToastStack extends LitElement {
  static override styles = css`
    :host {
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 10000;
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-width: min(420px, calc(100vw - 48px));
      pointer-events: none;
    }

    :host([data-empty]) {
      display: none;
    }

    .toast {
      pointer-events: auto;
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: 12px;
      align-items: start;
      padding: 12px 14px;
      background: var(--void, #05080f);
      color: var(--ivory, #efe6d2);
      border: 1px solid var(--brass-faint, rgba(192, 138, 62, 0.18));
      border-left-width: 3px;
      border-left-color: var(--brass, #c08a3e);
      box-shadow:
        0 8px 32px rgba(0, 0, 0, 0.55),
        0 1px 0 rgba(255, 255, 255, 0.04);
      animation: toast-in 220ms ease-out;
    }

    .toast[data-kind="error"] {
      border-left-color: var(--brass-hot, #e8b35c);
    }

    .toast[data-kind="warning"] {
      border-left-color: var(--brass, #c08a3e);
    }

    .toast[data-kind="info"] {
      border-left-color: var(--ivory-faint, rgba(239, 230, 210, 0.28));
    }

    .toast[data-kind="success"] {
      border-left-color: var(--ivory, #efe6d2);
    }

    .icon {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      margin-top: 1px;
      font-size: 14px;
      line-height: 1;
      color: inherit;
      text-align: center;
    }

    .toast[data-kind="error"] .icon {
      color: var(--brass-hot, #e8b35c);
    }

    .body {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .title {
      font-weight: 600;
      font-size: 13px;
      line-height: 1.3;
      color: var(--ivory, #efe6d2);
    }

    .message {
      font-size: 12px;
      line-height: 1.4;
      color: var(--ivory-dim, rgba(239, 230, 210, 0.6));
      word-wrap: break-word;
    }

    .close {
      flex-shrink: 0;
      background: transparent;
      border: none;
      cursor: pointer;
      color: var(--ivory-dim, rgba(239, 230, 210, 0.6));
      padding: 0;
      width: 44px;
      height: 44px;
      font-family: inherit;
      font-size: 14px;
      line-height: 1;
      transition: color 120ms ease-out;
    }

    .close:hover,
    .close:focus-visible {
      color: var(--ivory, #efe6d2);
      outline: none;
    }

    .close:focus-visible {
      outline: 1px solid var(--brass, #c08a3e);
      outline-offset: 1px;
    }

    @keyframes toast-in {
      from {
        transform: translateY(8px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .toast {
        animation: none;
      }
    }
  `;

  @state()
  private _toasts: readonly Toast[] = [];

  private _disposeEffect: (() => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this._disposeEffect = effect(() => {
      this._toasts = toasts.value;
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._disposeEffect !== null) {
      this._disposeEffect();
      this._disposeEffect = null;
    }
  }

  override render(): TemplateResult | typeof nothing {
    if (this._toasts.length === 0) {
      this.setAttribute("data-empty", "");
      return nothing;
    }
    this.removeAttribute("data-empty");
    // Sprint 26 Lane 6 (R5-IA-8) — promote the wrapper region to
    // an explicit `aria-live="polite"` zone so the IA audit
    // (`tools/triage/ia-audit.mjs`) recognises the stack as a
    // canonical state-change indicator pattern. Each child toast
    // still carries its own role/aria-live — assistive tech
    // honours the more-specific child setting, so this is purely
    // additive.
    return html`
      <div
        role="region"
        aria-live="polite"
        aria-atomic="false"
        aria-label="${t("toast.region.label")}"
        style="display: contents;"
      >
        ${repeat(
          this._toasts,
          (toast) => toast.id,
          (toast) => this._renderToast(toast),
        )}
      </div>
    `;
  }

  private _renderToast(toast: Toast): TemplateResult {
    const role = toast.kind === "error" ? "alert" : "status";
    const ariaLive = toast.kind === "error" ? "assertive" : "polite";
    return html`
      <div
        class="toast"
        data-kind="${toast.kind}"
        data-toast-id="${toast.id}"
        role="${role}"
        aria-live="${ariaLive}"
      >
        <span class="icon" aria-hidden="true">${kindGlyph(toast.kind)}</span>
        <div class="body">
          ${toast.title !== undefined && toast.title !== ""
            ? html`<div class="title">${toast.title}</div>`
            : nothing}
          <div class="message">${toast.message}</div>
        </div>
        <button
          type="button"
          class="close"
          aria-label="${t("toast.dismiss.aria")}"
          @click=${() => {
            dismissToast(toast.id);
          }}
        >
          ✕
        </button>
      </div>
    `;
  }
}

function kindGlyph(kind: ToastKind): string {
  switch (kind) {
    case "error":
      return "✕";
    case "warning":
      return "!";
    case "info":
      return "i";
    case "success":
      return "✓";
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-toast-stack": VxaToastStack;
  }
}
